import React, { useState, useEffect } from 'react';
import { openDB, getTableData, addIndexedDBData, deleteIndexedDBData } from '@utility/indexedDB_utils';
import { IoPersonSharp } from "react-icons/io5";
import { GrUpdate } from "react-icons/gr";

const GeneralSettings = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [EditUserData, setEditUserData] = useState(false);
  const [EditUpdateSettings, setUpdateSettings] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const db = await openDB();
        const data = await getTableData('userdata');
        if (data && data.length > 0) {
          setUserData(data[0]);
        } else {
          // Initialize with default values if no user exists
          setUserData({
            first_name: '',
            surname: '',
            user_name: '',
            height: 0,
            updateOnSetComplete: false,
            updateOnWeightLog: false,
            updateOnWorkoutPlanChange: false,
            isMetric: true // Default to metric system
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleToggle = async (field) => {
  if (!userData) return;
  
  const updatedData = {
    ...userData,
    [field]: !userData[field]
    };
    
    setUserData(updatedData);
    
    try {
      const db = await openDB();
      const transaction = db.transaction('userdata', 'readwrite');
      const store = transaction.objectStore('userdata');
      
      // Update the existing record
      if (userData.id !== undefined) {
        await store.put(updatedData);
      } else {
        // If no ID exists (shouldn't happen), add as new record
        await store.add(updatedData);
      }
    } catch (error) {
      console.error('Error updating user data:', error);
      // Revert if update fails
      setUserData(userData);
    }
  };

  const handleHeightChange = async (e) => {
    if (!userData) return;
    
    const newHeight = parseInt(e.target.value);
    if (isNaN(newHeight)) return;
    
    const updatedData = {
      ...userData,
      height: newHeight
    };
    
    setUserData(updatedData);
    
    try {
      const db = await openDB();
      const transaction = db.transaction('userdata', 'readwrite');
      const store = transaction.objectStore('userdata');
      
      // Update the existing record
      if (userData.id !== undefined) {
        await store.put(updatedData);
      } else {
        // If no ID exists (shouldn't happen), add as new record
        await store.add(updatedData);
      }
    } catch (error) {
      console.error('Error updating user height:', error);
      // Revert if update fails
      setUserData(userData);
    }
  };

  if (loading) {
    return <div>Loading user settings...</div>;
  }

  if (!userData) {
    return <div>No user data fo und</div>;
  }

  return (
    <div className="settings-content">
      <h2>General Settings</h2>

      <button onClick={() => { setEditUserData(true);}} className="profile-button">
        <IoPersonSharp />Edit User Details
      </button>

      <div className="gap"></div>

      <button onClick={() => { setUpdateSettings(true);}} className="profile-button">
        <GrUpdate/>Edit Update Settings
      </button>


      {EditUserData && (
        <div className="modal-overlay">
          <div className="modal-content confirmation-dialog">
            <h3 className="dialog-title">Edit User Data</h3>
                <div className="form-group width92p5">
                  <label>Username:</label>
                  <input 
                    type="text" 
                    value={userData.user_name || ''} 
                    onChange={(e) => setUserData({...userData, user_name: e.target.value})}
                  />
                </div>
                
                <div className="form-group width92p5">
                  <label>Height ({userData.isMetric ? 'cm' : 'in'}):</label>
                  <input
                    type="text" 
                    value={userData.height} 
                    onChange={handleHeightChange}
                  />
                </div>

            <div className="gap"></div>

            <div className="button-box">
              <button onClick={() => { setEditUserData(false);}} className="cancel-button">
                Cancel
              </button>

              <button onClick={() => { setEditUserData(false);}} className="edit-button">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {EditUpdateSettings && (
        <div className="modal-overlay">
          <div className="modal-content confirmation-dialog">
            <h3 className="dialog-title">Edit User Details</h3>
            <div className="form-group">
              <label>Auto-update on set completion:</label>
              <div
                className={`toggle-switch ${userData.updateOnSetComplete ? 'isolated' : 'compound'}`}
                onClick={() => handleToggle('updateOnSetComplete')}
                role="switch"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleToggle('updateOnSetComplete');
                  }
                }}
                aria-checked={userData.updateOnSetComplete}
              >
                <span className="label-isolated">On</span>
                <span className="toggle-thumb"></span>
                <span className="label-compound">Off</span>
              </div>

            </div>
            
            <div className="form-group">
        <label>Auto-update on weight log:</label>
        <div
          className={`toggle-switch ${userData.updateOnWeightLog ? 'isolated' : 'compound'}`}
          onClick={() => handleToggle('updateOnWeightLog')}
          role="switch"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggle('updateOnWeightLog');
            }
          }}
          aria-checked={userData.updateOnWeightLog}
        >
          <span className="label-isolated">On</span>
          <span className="toggle-thumb"></span>
          <span className="label-compound">Off</span>
        </div>
            </div>

            <div className="form-group">
              <label>Auto-update on workout plan change:</label>
              <div
                className={`toggle-switch ${userData.updateOnWorkoutPlanChange ? 'isolated' : 'compound'}`}
                onClick={() => handleToggle('updateOnWorkoutPlanChange')}
                role="switch"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleToggle('updateOnWorkoutPlanChange');
                  }
                }}
                aria-checked={userData.updateOnWorkoutPlanChange}
              >
                <span className="label-isolated">On</span>
                <span className="toggle-thumb"></span>
                <span className="label-compound">Off</span>
              </div>
            </div>
            
            <div className="form-group">
              <label>Measurement System:</label>
              <div
                className={`toggle-switch ${userData.isMetric ? 'isolated' : 'compound'}`}
                onClick={() => handleToggle('isMetric')}
                role="switch"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleToggle('isMetric');
                  }
                }}
                aria-checked={userData.isMetric}
              >
                <span className="label-isolated">Metric</span>
                <span className="toggle-thumb"></span>
                <span className="label-compound">Imperial</span>
              </div>
            </div>
            <div className="gap"></div>
            <button onClick={() => { setUpdateSettings(false);}} className="profile-button red-button">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralSettings;